/* eslint-disable react/no-multi-comp */
import React, { useEffect, useState } from "react"
import { useLockBodyScroll, useMedia } from "react-use"
import { graphql } from "gatsby"
import { useSassUnitValues } from "@jsUtils"
import { Meta, ResourcesFilter, ResourcesArticles, ResourcesHeader } from "@shared"
import { Footer, GetInTouch, Header, PageSection } from "@components/sections"
import { CONSTANTS, CONTENT } from "@data"
import styles from "@pageStyles/resources.module.scss"

const { BREAKPOINTS, COLORS, THEMES, PAGE_TRACKER_KEYS, PAGE_TRACKER_VALUES } = CONSTANTS
const { RESOURCES } = CONTENT

const GRID_BREAKPOINT = `(min-width: ${useSassUnitValues(BREAKPOINTS.DESKTOP)}px)`

const PAGINATION_INCREMENT = 12

const VALID_CATEGORIES = ["blog", "event", "exo works", "guides", "podcast"]

const ResourcesPagination = ({ items, pagination, setPagination }) => {
  // adjust article count if a featured article exists
  const hasFeatured = items.some(item => item.featuredArticle === true)

  const articleCount = hasFeatured ? items.length - 1 : items.length

  // if all articles are already showing, render nothing
  if (articleCount <= pagination) {
    return null
  }

  return (
    <button
      className={styles.more}
      onClick={() => setPagination(pagination + PAGINATION_INCREMENT)}
      type="button"
    >
      View More
    </button>
  )
}

// parses window.location.hash into a filter string
const parseHashToFilter = () => {
  // 1. convert string to an array of letters
  // 2. remove # from front of array
  // 3. convert all dashes to spaces
  // 4. convert array of letters back to a string
  return window.location.hash
    .split("")
    .slice(1)
    .map(character => (character === "-" ? " " : character))
    .join("")
}

// checks that an array of categories includes at least 1 belonging to Resources
const includesResourcesCategory = categories =>
  categories.some(category => VALID_CATEGORIES.includes(category))

const ResourcesPage = props => {
  const [filter, setFilter] = useState(null)
  const [menu, setMenu] = useState(false)
  const [pagination, setPagination] = useState(PAGINATION_INCREMENT)
  const isGridLayout = useMedia(GRID_BREAKPOINT)

  const { data } = props
  const { allContentfulArticle: articles, globalMeta, pageMeta } = data

  // remove articles without Resources categories, and apply the filter
  const items = articles.nodes.filter(article => {
    // start with primary category
    let categories = [article.category]

    // push secondary categories if they exist
    if (article.tags) {
      categories.push(...article.tags)
    }

    // convert the categories to lowercase
    categories = categories.map(category => category.toLowerCase())

    // remove duplicates
    categories = categories.filter((category, index) => categories.indexOf(category) === index)

    // check for a valid Resources category
    const valid = includesResourcesCategory(categories)

    // if a filter exists, check that it is included in the categories
    const matchesFilter = filter ? categories.includes(filter) : true

    return valid && matchesFilter
  })

  // update filter based on hash
  useEffect(() => {
    const handleHashchange = () => {
      const parsed = parseHashToFilter()

      // catch "The Latest"
      // hash is an empty string, but the filter needs to be null
      if (!parsed) {
        setFilter(null)
        return
      }

      setFilter(parsed)
    }

    // handle initial hash
    handleHashchange()

    // watch for hash changes
    window.addEventListener("hashchange", handleHashchange)

    return () => {
      window.removeEventListener("hashchange", handleHashchange)
    }
  }, [])

  // lock scroll based on filter menu open / closed
  useLockBodyScroll(menu)

  return (
    <>
      <Meta
        base={globalMeta}
        page={{ pageMeta }}
        staticTitle={RESOURCES.title}
        hubSpotObj={{
          contentTypeKey: PAGE_TRACKER_KEYS.RESOURCES_HOMEPAGE,
          contentTypeValue: PAGE_TRACKER_VALUES.BLOG_POST
        }}
      />

      <Header pageColor={COLORS.FLURO} showGridLines={false} theme={THEMES.LIGHT} />
      <PageSection theme={THEMES.LIGHT}>
        <div className={styles.container}>
          <ResourcesFilter
            filter={filter}
            isGridLayout={isGridLayout}
            menu={menu}
            setMenu={setMenu}
          />
          <ResourcesHeader filter={filter} menu={menu} setMenu={setMenu} />
          <ResourcesArticles items={items} pagination={pagination} />
          <ResourcesPagination
            items={items}
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
      </PageSection>

      <GetInTouch color={COLORS.FLURO} theme={THEMES.DARK} />
      {/* Don't remove link below, it's purpose is to improve SEO */}
      <a style={{ visibility: "hidden" }} href="/resources/all">
        All
      </a>
      <Footer />
    </>
  )
}

export const pageQuery = graphql`
  {
    allContentfulArticle(sort: { order: DESC, fields: date }) {
      nodes {
        category
        date
        featuredArticle
        id
        slug
        source
        thumbnail {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
        tags
        title
      }
    }
    globalMeta: contentfulPageMeta(id: { eq: "423babd8-0278-5b2e-85f6-77fb7ee55bee" }) {
      ...GlobalMetaData
    }
    pageMeta: contentfulPageMeta(contentful_id: { eq: "535TPKnQxjqzWoXAHoChnw" }) {
      ...PageMetaData
    }
  }
`

export default ResourcesPage
